import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { api } from "@/api/client";
import type { HandoffPayload } from "@/api/types";
import { APPS } from "@/data/apps";
import { appForClient } from "@/sso/clientApps";
import type { AppId } from "@/types";
import { BrandMark, PrimaryButton, Spinner, ErrorNote } from "@/components/auth/ui";

const TOTAL_MS = 10_000;

// TEMP: `?demo=<appId>` renders the modal with a fabricated handoff so the
// redirect experience can be previewed without a real child-app sign-in.
// Finishing the countdown returns to the desktop. Remove when no longer needed.
const DEMO_CLIENTS: Partial<Record<AppId, string>> = {
  ares: "ares-ide",
  athena: "athena-web",
  byonc: "byonc-console",
  garlic: "garlic-web",
  appgraph: "appgraph-web",
};

function makeDemoPayload(appId: string): HandoffPayload | null {
  const clientId = DEMO_CLIENTS[appId as AppId];
  if (!clientId) return null;
  const app = APPS[appId as AppId];
  const host = `${app.id}.momentum-ai.dev`;
  return {
    redirectUrl: `https://${host}/auth/callback?code=demo-0Af3xQ7hPw&state=x1fPq9&iss=${encodeURIComponent(
      window.location.origin,
    )}`,
    clientId,
    appName: app.name,
    appUri: `https://${host}`,
    logoUri: null,
    displayHost: host,
  };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

/** Product accents used to tint the desktop silhouette (mirrors the launchpad). */
const GHOST_ACCENTS = (["ares", "garlic", "athena", "byonc", "appgraph"] as const).map(
  (id) => APPS[id].accent,
);

function GhostTile({ accent, size = 36 }: { accent: string; size?: number }) {
  return (
    <span
      className="rounded-xl shadow-lg"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
    />
  );
}

/** Content-free glass panel suggesting a desktop widget under the frost. */
function GhostPanel({ className, accent }: { className: string; accent?: string }) {
  return (
    <div className={`glass absolute rounded-2xl border border-app bg-surface/60 p-5 ${className}`}>
      <div className="space-y-3">
        <div className="h-2.5 w-1/3 rounded-full bg-surface-2" />
        <div className="h-2.5 w-3/4 rounded-full bg-surface-2" />
        <div className="h-2.5 w-1/2 rounded-full bg-surface-2" />
        {accent && (
          <div
            className="h-8 w-28 rounded-lg"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * A silhouette of the Hive desktop (hero, widget columns, launchpad, dock)
 * rendered under a frost layer, so the redirect modal reads as floating over
 * the OS rather than on a blank page. Pure decoration — no data, no interaction.
 */
function DesktopBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="mesh-bg absolute inset-0" />
      <div className="grid-pattern absolute inset-0 opacity-[0.55]" />

      <div className="absolute left-1/2 top-[7%] -translate-x-1/2 text-center">
        <div className="text-[11px] font-medium uppercase tracking-[0.4em] text-app-2">
          Momentum AI · Workspace
        </div>
        <div className="mt-2 text-5xl font-semibold tracking-tight text-app">Welcome to Hive</div>
      </div>

      <GhostPanel className="left-10 top-[88px] h-[170px] w-[300px]" accent="#f59e0b" />
      <GhostPanel className="left-10 top-[290px] h-[230px] w-[370px]" accent="#14b8a6" />
      <GhostPanel className="right-10 top-[88px] h-[240px] w-[370px]" accent="#f97316" />
      <GhostPanel className="right-10 top-[360px] h-[200px] w-[370px]" accent="#06b6d4" />

      {/* Launchpad silhouette — the five product tiles */}
      <div className="glass absolute left-1/2 top-[196px] h-[200px] w-[610px] -translate-x-1/2 rounded-2xl border border-app bg-surface/60 p-5">
        <div className="h-2.5 w-1/4 rounded-full bg-surface-2" />
        <div className="mt-4 flex gap-6">
          {GHOST_ACCENTS.map((accent) => (
            <GhostTile key={accent} accent={accent} size={42} />
          ))}
        </div>
      </div>

      {/* Dock silhouette */}
      <div className="glass absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-3 rounded-2xl border border-app bg-surface/60 p-3">
        {GHOST_ACCENTS.map((accent) => (
          <GhostTile key={accent} accent={accent} size={32} />
        ))}
      </div>

      {/* The frost — everything above blurs into a dashboard silhouette */}
      <div className="absolute inset-0 bg-[hsl(var(--bg-0)/0.3)] backdrop-blur-[14px]" />

      {/* Accent halo lifting the modal off the frost */}
      <div
        className="absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.5), transparent 70%)" }}
      />
    </div>
  );
}

/** Frosted-desktop stage + centered glass card shared by every page state. */
function RedirectShell({ children }: { children: ReactNode }) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <div className="relative flex min-h-screen w-screen items-center justify-center overflow-hidden bg-[hsl(var(--bg-0))] px-5 py-10 text-app">
      <DesktopBackdrop />
      <motion.main
        initial={reducedMotion ? false : { opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        className="glass-strong relative z-10 w-full max-w-[430px] rounded-3xl border border-app-strong p-8 shadow-window"
      >
        {children}
      </motion.main>
    </div>
  );
}

/** The destination app's mark: catalog tile → registered logo → Hive mark. */
function AppMark({ data }: { data: HandoffPayload }) {
  const app = appForClient(data.clientId);
  if (app) {
    return (
      <div
        className="grid h-11 w-11 place-items-center rounded-xl text-white shadow-lg"
        style={{ background: `linear-gradient(135deg, ${app.accent}, ${app.accent}aa)` }}
      >
        <app.icon size={20} />
      </div>
    );
  }
  if (data.logoUri) {
    return <img src={data.logoUri} alt="" className="h-11 w-11 rounded-xl object-cover shadow-lg" />;
  }
  return <BrandMark size={44} />;
}

/**
 * The post-login redirection modal. It fetches the SINGLE-USE handoff (the only
 * place the server-built final URL is revealed), shows the destination app + the
 * literal URL, counts down 10s with a progress bar, and navigates. Hovering or
 * focusing pauses the countdown; reduced-motion users get a static bar; the
 * countdown is announced to screen readers. Navigation ALWAYS uses the
 * server-built `redirectUrl` — never anything from the address bar.
 */
export function RedirectModalPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const handoff = params.get("handoff");
  const reducedMotion = usePrefersReducedMotion();

  // TEMP: simulated preview (see DEMO_CLIENTS above).
  const demoParam = params.get("demo");
  const demoData = demoParam ? makeDemoPayload(demoParam) : null;

  const { data: fetched, status } = useQuery<HandoffPayload>({
    queryKey: ["handoff", handoff],
    queryFn: () => api.get<HandoffPayload>(`/oidc/handoff/${handoff}`),
    enabled: !!handoff && !demoData,
    retry: false,
    // Single-use: never refetch (a second fetch would 404).
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const data = demoData ?? fetched;

  const [remaining, setRemaining] = useState(TOTAL_MS);
  const [paused, setPaused] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const navigated = useRef(false);
  const autoAttempted = useRef(false);

  // Open the app in ITS OWN tab, then dismiss this modal so the tab it was
  // launched from lands on the Hive desktop — sign-in ends with both the
  // product and Hive on screen.
  function go(url: string) {
    if (navigated.current) return;
    // A simulation has nowhere real to go — return to the desktop instead.
    if (demoData) {
      navigated.current = true;
      navigate("/", { replace: true });
      return;
    }
    const opened = window.open(url, "_blank");
    if (opened) {
      opened.opener = null;
      navigated.current = true;
      navigate("/", { replace: true });
    } else {
      // The countdown fired without a user gesture and the browser blocked the
      // tab. Hold here — the button click is a gesture and always succeeds.
      setPopupBlocked(true);
      setPaused(true);
    }
  }

  // Drive the countdown once the payload is loaded.
  useEffect(() => {
    if (!data) return;
    let last = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      if (!paused) setRemaining((r) => Math.max(0, r - dt));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [data, paused]);

  useEffect(() => {
    if (!data || remaining > 0 || autoAttempted.current) return;
    autoAttempted.current = true;
    go(data.redirectUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, remaining]);

  if (!handoff && !demoData) {
    return (
      <RedirectShell>
        <div className="text-center">
          <h1 className="text-xl font-semibold text-app">Nothing to redirect</h1>
          <p className="mt-1.5 text-sm text-app-1">This page expects a sign-in handoff.</p>
          <Link to="/" className="mt-4 block text-sm text-app-1 hover:underline">
            Return to Hive
          </Link>
        </div>
      </RedirectShell>
    );
  }

  if (!demoData && status === "pending") {
    return (
      <RedirectShell>
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <Spinner size={24} />
          <h1 className="text-xl font-semibold text-app">Finishing sign-in…</h1>
          <p className="text-sm text-app-1">One moment</p>
        </div>
      </RedirectShell>
    );
  }

  if (!data || (!demoData && status === "error")) {
    return (
      <RedirectShell>
        <div className="text-center">
          <h1 className="mb-3 text-xl font-semibold text-app">This redirect has expired</h1>
          <ErrorNote>
            The sign-in link was already used or timed out. Please return to the application and
            start the sign-in again.
          </ErrorNote>
          <Link to="/" className="mt-2 block text-sm text-app-1 hover:underline">
            Return to Hive
          </Link>
        </div>
      </RedirectShell>
    );
  }

  const seconds = Math.ceil(remaining / 1000);
  const progress = 1 - remaining / TOTAL_MS;
  const accent = appForClient(data.clientId)?.accent;

  return (
    <RedirectShell>
      <div
        className="flex flex-col items-center"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {demoData && (
          <span className="mb-4 rounded-full border border-app px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-app-2">
            Simulation — no real redirect
          </span>
        )}

        {/* Destination app identity */}
        <div className="flex items-center gap-3">
          <AppMark data={data} />
          <span className="text-lg font-semibold text-app">{data.appName}</span>
        </div>

        <h1 className="mt-5 text-center text-2xl font-semibold tracking-tight text-app">
          {popupBlocked ? (
            <>Ready to open {data.appName}</>
          ) : (
            <>
              Opening {data.appName} in <span className="tabular-nums">{seconds}</span>{" "}
              {seconds === 1 ? "second" : "seconds"}
            </>
          )}
        </h1>

        {/* Countdown bar */}
        <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress * 100}%`,
              background: accent
                ? `linear-gradient(90deg, ${accent}, ${accent}aa)`
                : "linear-gradient(90deg, hsl(var(--accent)), hsl(var(--accent) / 0.65))",
              transition: reducedMotion ? undefined : "width 80ms linear",
            }}
          />
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-sm text-app-1">
          <ShieldCheck size={15} className="accent" /> Signed in securely with Hive
        </p>

        <p aria-live="polite" className="sr-only">
          {popupBlocked
            ? `Your browser blocked the new tab. Use the button to open ${data.appName}.`
            : paused
              ? "Redirect paused."
              : `Opening ${data.appName} in a new tab in ${seconds} seconds.`}
        </p>

        {/* The literal, server-validated URL it will navigate to */}
        <div className="mb-5 mt-5 w-full">
          <div className="mb-1 text-center text-xs uppercase tracking-wide text-app-2">Destination</div>
          <div className="break-all rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3 py-2 text-center font-mono text-xs text-app-1">
            {data.redirectUrl}
          </div>
        </div>

        <PrimaryButton
          type="button"
          onClick={() => go(data.redirectUrl)}
          className="w-full"
          aria-label={`Continue to ${data.appName} now`}
        >
          Continue to {data.appName} now
        </PrimaryButton>
        <p className="mt-3 text-center text-xs text-app-2">
          {popupBlocked
            ? "Your browser blocked the new tab — the button above opens it."
            : `${data.appName} opens in a new tab; this one stays on your Hive desktop. ${
                paused ? "Countdown paused — hover off to resume." : "Hover to pause the countdown."
              }`}
        </p>
      </div>
    </RedirectShell>
  );
}
